/**
 * Exact normalized substring match for one governed PDF page (presentation-only).
 *
 * No trim ladders, fuzzy matching, semantic similarity, or cross-page search.
 */

import { canonicalizePdfPageText } from "./cognitoryPdfPageTextCanonicalization";
import { normalizeCitedPassageWhitespace } from "./cognitoryGovernedPdfPassageHighlight";

export type ExactGovernedPassagePageMatch =
  | {
      kind: "matched";
      start: number;
      end: number;
      matchedText: string;
    }
  | { kind: "not_found" }
  | { kind: "ambiguous"; matchCount: number };

export function normalizeGovernedCitedPassageForPageMatch(
  citedPassage: string,
): string | null {
  const whitespaceNormalized = normalizeCitedPassageWhitespace(citedPassage);
  if (!whitespaceNormalized) {
    return null;
  }
  return canonicalizePdfPageText(whitespaceNormalized);
}

export function resolveExactGovernedPassagePageMatch(input: {
  canonicalPageText: string;
  citedPassage: string;
}): ExactGovernedPassagePageMatch {
  const normalizedPassage = normalizeGovernedCitedPassageForPageMatch(
    input.citedPassage,
  );
  if (!normalizedPassage) {
    return { kind: "not_found" };
  }

  const pageText = input.canonicalPageText;
  let searchIndex = 0;
  let firstStart = -1;
  let matchCount = 0;

  while (searchIndex <= pageText.length) {
    const foundAt = pageText.indexOf(normalizedPassage, searchIndex);
    if (foundAt === -1) {
      break;
    }
    if (matchCount === 0) {
      firstStart = foundAt;
    }
    matchCount++;
    searchIndex = foundAt + 1;
  }

  if (matchCount === 0) {
    return { kind: "not_found" };
  }
  if (matchCount > 1) {
    return { kind: "ambiguous", matchCount };
  }

  const end = firstStart + normalizedPassage.length;
  return {
    kind: "matched",
    start: firstStart,
    end,
    matchedText: pageText.slice(firstStart, end),
  };
}
